import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";
import Select from "react-select";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  createSymptomAction,
  updateSymptomAction,
} from "../../../store/actions/SymptomAction";
import { showErrorAlert } from "../../../services/ErrorHandlingService";
import { getDiseasesAction } from "../../../store/actions/DiseasesAction"
import { getMedicinesAction } from "../../../store/actions/MedicinesAction";

import Loader from "../Common/Loader";

const AddSymptom = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  // const { id } = useParams();
  // const symptomList = useSelector((state) => state.symptoms?.symptoms || []);
  // const symptom = symptomList.find((s) => String(s.id) === String(id));
  const diseases = useSelector((state) => state.diseases?.diseases || [])
  const medicines = useSelector(
    (state) => state.medicines?.medicines?.medicines || []
  );

  // const isEditMode = !!symptom;
  const { id } = useParams();

  const symptomList = useSelector((state) => state.symptoms?.symptoms || []);

  const symptom = symptomList.find(
    (s) => String(s.symptom_id) === String(id)
  );

  const isEditMode = !!id;
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const [diseaseMedicineMap, setDiseaseMedicineMap] = useState({});

  const [selectedDiseases, setSelectedDiseases] = useState([])
  const [form, setForm] = useState({
    symptomName: "",
    description: "",
    status: "Active",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [errors, setErrors] = useState({});

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  // Initialize form in edit mode
  useEffect(() => {
    if (isEditMode && symptom && diseases.length && medicines.length) {
      setFetching(true);
      setErrors({});

      // 1️⃣ Preselect Diseases
      const diseaseSelections =
        symptom.disease?.map((d) => ({
          value: String(d.disease_id),
          label: d.disease_name,
        })) || [];

      setSelectedDiseases(diseaseSelections);

      // 2️⃣ Preselect Disease → Medicines mapping
      const medicineMap = {};

      (symptom.disease_medicines || []).forEach((dm) => {
        medicineMap[String(dm.disease_id)] =
          dm.medicines?.map((m) => ({
            value: String(m.medicine_id),
            label: m.medicine_name,
          })) || [];
      });

      setDiseaseMedicineMap(medicineMap);

      // 3️⃣ Set form fields
      setForm({
        symptomName: symptom.symptom_name || "",
        description: symptom.symptom_description || "",
        status: symptom.symptom_status || "",
      });

      setFetching(false);
    }
  }, [isEditMode, symptom, diseases, medicines]);

  useEffect(() => {
    setFetching(true);

    Promise.all([
      dispatch(getDiseasesAction(false)),
      dispatch(getMedicinesAction(false))
    ])
      .then(() => setFetching(false))
      .catch(() => setFetching(false));
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // clear error instantly
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleDiseaseChange = (selectedOptions) => {
    setSelectedDiseases(selectedOptions)
  }
  const handleStatusChange = (selectedOption) => {
    setForm((prev) => ({
      ...prev,
      status: selectedOption ? selectedOption.value : "",
    }));

    setErrors((prev) => ({
      ...prev,
      status: "",
    }));
  };

  // const validate = () => {
  //   const newErrors = {};
  //   if (!form.symptomName.trim()) newErrors.symptomName = "Please Enter name";

  //   if (!form.status) newErrors.status = "Status is required";

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validate required fields
    const newErrors = {};
    if (!form.symptomName.trim())
      newErrors.symptomName = "Symptom Name is required";

    if (!form.status)
      newErrors.status = "Status is required";

    if (!selectedDiseases || selectedDiseases.length === 0)
      newErrors.diseases = "Please select at least one disease";

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setLoading(false);
      return;
    }

    try {
      // build diseases_ids CSV and disease_medicines array
      const diseases_ids = (selectedDiseases || []).map((d) => d.value).join(",")
      const disease_medicines = Object.keys(diseaseMedicineMap || {}).map((dId) => {
        const meds = diseaseMedicineMap[dId] || []
        const medIds = Array.isArray(meds)
          ? meds.map((m) => (m && m.value ? Number(m.value) : Number(m)))
          : []
        return { disease_id: Number(dId), medicines: medIds }
      })

      const payload = { ...form, diseases_ids, disease_medicines }

      if (isEditMode) {
        await dispatch(updateSymptomAction(payload, id, navigate));
      } else {
        await dispatch(createSymptomAction(payload, navigate));
      }
    } catch (error) {
      console.error("Form submission failed:", error);
      showErrorAlert(
        isEditMode ? "Failed to update symptom" : "Failed to add symptom"
      );
    } finally {
      setLoading(false); //  Always stop loading
    }
  };

  if (fetching) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-xl-12">
        <div className="d-flex justify-content-end p-3">
          <Link to="/symptoms">
            <Button variant="primary">Back</Button>
          </Link>
        </div>
        <div className="card">
          <div className="card-body">
            <h4 className="mb-4">
              {isEditMode ? "Edit Symptom" : "Add New Symptom"}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Symptom Name<span style={{ color: "red" }}>*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="symptomName"
                    placeholder="Enter Symptom Name"
                    value={form.symptomName}
                    onChange={handleChange}
                  //required
                  />

                  {errors.symptomName && <small className="text-danger">{errors.symptomName}</small>}

                </div>
                <div className="col-xl-6 mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    placeholder="Enter Description (optional)"
                    value={form.description}
                    onChange={handleChange}
                  ></textarea>
                </div>
              </div>

              <div className="row">

                {/* Disease Dropdown */}
                <div className="col-xl-6 mb-3">
                  <label className="form-label">
                    Disease <span style={{ color: "red" }}>*</span>
                  </label>

                  <Select
                    isMulti
                    name="diseases"
                    options={diseases.map((d) => ({
                      value: String(d.disease_id),
                      label: d.disease_name,
                    }))}
                    value={selectedDiseases}
                    onChange={(selected) => {
                      setSelectedDiseases(selected);

                      // clear disease error
                      setErrors((prev) => ({
                        ...prev,
                        diseases: "",
                      }));

                      setDiseaseMedicineMap((prev) => {
                        const updated = {};
                        selected?.forEach((d) => {
                          if (prev[d.value]) updated[d.value] = prev[d.value];
                        });
                        return updated;
                      });
                    }}

                    placeholder="Select Disease(s)"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuShouldScrollIntoView={false}

                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "50px",
                        borderColor: errors.diseases ? "#dc3545" : base.borderColor,
                        boxShadow: errors.diseases
                          ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                          : base.boxShadow,
                      }),

                      multiValue: (base) => ({
                        ...base,
                        backgroundColor: "#eef2f6",
                        padding: "2px",
                        borderRadius: "4px",
                      }),

                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />

                  {errors.diseases && (
                    <small className="text-danger mt-1 d-block">{errors.diseases}</small>
                  )}
                </div>

                <div className="col-xl-6 mb-3">
                  <label className="form-label">Status<span style={{ color: "red" }}>*</span>
                  </label>
                  <Select
                    options={statusOptions}
                    value={statusOptions.find(
                      (opt) => opt.value === form.status
                    )}
                    onChange={handleStatusChange}
                    placeholder="Select Status"
                    className={errors.status ? "is-invalid" : ""}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: "50px",
                        height: "50px",
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        lineHeight: "50px",
                      }),
                      dropdownIndicator: (provided) => ({
                        ...provided,
                        padding: "4px",
                      }),
                      indicatorSeparator: (provided) => ({
                        ...provided,
                        display: "none",
                      }),
                    }}
                  />
                  {errors.status && (
                    <div className="invalid-feedback d-block">
                      {errors.status}
                    </div>
                  )}
                </div>
              </div>


              {/* EXTRA MEDICINE DROPDOWNS PER DISEASE */}
              <div className="row mt-4">
                {selectedDiseases?.length > 0 && (
                  <h5 className="fw-bold mb-3">Selected Diseases & Assigned Medicines</h5>
                )}

                {selectedDiseases?.map((disease) => (
                  <div className="col-xl-12 mb-3" key={disease.value}>
                    <div className="row align-items-center">

                      {/* LEFT: Disease Label */}
                      <div className="col-md-4">
                        <label className="form-label mb-0">
                          <strong>{disease.label}</strong>
                        </label>
                      </div>

                      {/* RIGHT: Multi-select Medicines */}
                      <div className="col-md-3">
                        <Select
                          isMulti
                          name={`medicine_for_${disease.value}`}
                          options={medicines.map((m) => ({
                            value: String(m.medicine_id),
                            label: m.medicine_name,
                          }))}
                          value={diseaseMedicineMap[disease.value] || []}
                          onChange={(selected) =>
                            setDiseaseMedicineMap((prev) => ({
                              ...prev,
                              [disease.value]: selected,
                            }))
                          }
                          placeholder={`Select medicine(s) for ${disease.label}`}
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "45px",
                            }),
                          }}
                        />
                      </div>

                    </div>
                  </div>
                ))}
              </div>




              <button type="submit" className="btn btn-primary" disabled={loading || fetching}>
                {loading ? (
                  <>
                    <Loader size="sm" />
                    <span className="ms-2">{isEditMode ? "Updating..." : "Submitting..."}</span>
                  </>
                ) : (
                  <>{isEditMode ? "Update Symptom" : "Add Symptom"}</>
                )}
              </button>

              <button
                type="button"
                className="btn btn-secondary ms-4"
                onClick={() => navigate("/symptoms")}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSymptom;
